import {testIdProps} from '@github-ui/test-id-props'
import {Button, VisuallyHidden} from '@primer/react'
import {ArrowUpRightIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {memo, type RefObject, useCallback, useId, useState} from 'react'

import {MergeabilitySidesheet} from './MergeabilitySidesheet'
import {presentationForStatus, Status, mergeabilityStatus} from '../helpers/mergeability-status'
import {useMergeBoxPageData, useMergeBoxPageDataQueryKey} from '../page-data/loaders/use-merge-box-page-data'
import {useMergeMethodContext} from '../contexts/MergeMethodContext'
import {DockedPanel, useIsDockedPanelOpen} from '@github-ui/pull-requests/contexts/DockedPanelContext'
import type {MergeBoxPageData} from '../page-data/payloads/merge-box'
import {useMergeabilityLiveUpdates, type MergeabilityLiveUpdateChannels} from '../hooks/use-mergeability-live-updates'
import {useStatusChecksPageDataWithoutError} from '../page-data/loaders/use-status-checks-page-data'
import type {StatusChecksPageData} from '../page-data/payloads/status-checks'
import styles from './MergeStatusButton.module.css'
import {useAnalytics} from '@github-ui/use-analytics'

interface MergeStatusButtonCommonProps {
  mergeStatusButtonRef: RefObject<HTMLButtonElement | null>
  toggleMergeabilitySidesheet: (isOpen: boolean) => void
}

/**
 * A button that displays the merge status of a pull request.
 * Opens the mergeability sidesheet.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the component name
export const MergeStatusButtonWrapper = memo(function MergeStatusButtonWrapper({
  channels,
  headSha,
  helpUrl,
  viewerLogin,
  isDockedMode,
  onToggleDockedPanel,
  mergeStatusButtonRef,
  size,
  variant,
  buttonClasses,
  showTrailingArrow,
}: {
  channels: MergeabilityLiveUpdateChannels
  headSha: string
  helpUrl: string
  viewerLogin: string
  /** When true, suppresses the MergeabilitySidesheet and delegates click to onToggleDockedPanel */
  isDockedMode: boolean
  /** Called on button click when isDockedMode is true */
  onToggleDockedPanel: () => void
  mergeStatusButtonRef: RefObject<HTMLButtonElement | null>
  size?: 'small' | 'medium'
  variant?: 'default' | 'invisible'
  buttonClasses?: string
  showTrailingArrow?: boolean
}) {
  const {mergeMethod} = useMergeMethodContext()
  const {data} = useMergeBoxPageData({mergeMethod, bypassRequirements: false})
  const queryKey = useMergeBoxPageDataQueryKey()
  const {data: statusChecksData} = useStatusChecksPageDataWithoutError({
    pullRequestHeadSha: headSha,
  })

  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)
  const [mergeabilitySidesheetIsOpen, setMergeabilitySidesheetIsOpen] = useState(false)
  const toggleMergeabilitySidesheet = useCallback(
    (isOpen: boolean) => {
      if (isOpen && isDockedMode) {
        onToggleDockedPanel()
        return
      }
      setMergeabilitySidesheetIsOpen(isOpen)
    },
    [isDockedMode, onToggleDockedPanel],
  )

  useMergeabilityLiveUpdates({queryKey, channels})

  const isLoading = !data || !statusChecksData

  return (
    <>
      <MergeStatusButton
        className={buttonClasses}
        isLoading={isLoading}
        pullRequest={data?.pullRequest}
        mergeRequirements={data?.mergeRequirements}
        statusChecksData={statusChecksData}
        mergeStatusButtonRef={mergeStatusButtonRef}
        toggleMergeabilitySidesheet={toggleMergeabilitySidesheet}
        isPanelOpen={isDockedMode ? isDockedPanelOpen : mergeabilitySidesheetIsOpen}
        size={size}
        variant={variant}
        showTrailingArrow={showTrailingArrow}
      />
      {!isDockedMode && (
        <MergeabilitySidesheet
          helpUrl={helpUrl}
          mergeStatusButtonRef={mergeStatusButtonRef}
          mergeabilitySidesheetIsOpen={mergeabilitySidesheetIsOpen}
          toggleMergeabilitySidesheet={toggleMergeabilitySidesheet}
          viewerLogin={viewerLogin}
          channels={channels}
        />
      )}
    </>
  )
})

export type MergeStatusButtonData = MergeBoxPageData

interface MergeStatusButtonProps extends MergeStatusButtonCommonProps {
  isLoading?: boolean
  pullRequest?: MergeBoxPageData['pullRequest']
  mergeRequirements?: MergeBoxPageData['mergeRequirements']
  statusChecksData?: StatusChecksPageData
  isPanelOpen: boolean
  size?: 'small' | 'medium'
  variant?: 'default' | 'invisible'
  className?: string
  showTrailingArrow?: boolean
}

export function MergeStatusButton({
  isLoading,
  pullRequest,
  mergeRequirements,
  statusChecksData,
  mergeStatusButtonRef,
  toggleMergeabilitySidesheet,
  isPanelOpen,
  size = 'medium',
  className,
  variant = 'default',
  showTrailingArrow,
}: MergeStatusButtonProps) {
  const {sendAnalyticsEvent} = useAnalytics()
  const statusDescriptionId = useId()

  if (!mergeStatusButtonRef) return null

  let status = null

  if (!isLoading && pullRequest) {
    status = mergeabilityStatus({pullRequest, mergeRequirements: mergeRequirements ?? null, statusChecksData})
  }

  const defaultPresentation = {
    icon: undefined,
    iconColor: undefined,
    iconColorClass: undefined,
  }

  const statusPresentation = status ? presentationForStatus(status) : undefined
  const presentation =
    !status || status === Status.NonactionableFailure || status === Status.Unknown
      ? defaultPresentation
      : statusPresentation
  const statusDescription = status && status !== Status.Unknown ? statusPresentation?.title : undefined

  return (
    <>
      <Button
        ref={mergeStatusButtonRef}
        leadingVisual={presentation?.icon}
        trailingVisual={showTrailingArrow ? ArrowUpRightIcon : undefined}
        variant={variant}
        aria-expanded={isPanelOpen}
        aria-describedby={statusDescription ? statusDescriptionId : undefined}
        {...testIdProps('merge-status-button')}
        className={clsx(
          styles.mergeStatusButton,
          presentation?.iconColorClass && styles[presentation.iconColorClass],
          className,
        )}
        onClick={() => {
          if (toggleMergeabilitySidesheet) {
            toggleMergeabilitySidesheet(true)
            sendAnalyticsEvent('merge_status_side_panel.open', 'MERGE_STATUS_SIDE_PANEL_BUTTON')
          }
        }}
        size={size}
        loading={isLoading || status === Status.Unknown}
        loadingAnnouncement="Loading merge status"
        inactive={isLoading || status === Status.Unknown}
      >
        <span className={styles.labelFull}>Merge</span> Status
      </Button>
      {statusDescription && <VisuallyHidden id={statusDescriptionId}>{statusDescription}</VisuallyHidden>}
    </>
  )
}
